import { useState } from "react";
import { motion } from "motion/react";
import {
  RefreshCw,
  Droplets,
  Thermometer,
  Weight,
  Package2,
  TrendingUp,
  CheckCircle,
  ChevronRight,
  Download,
  Calendar,
  MapPin,
} from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import Toast from "./Toast";
import ImageGalleryModal from "./ImageGalleryModal";
import MaterialDetailModal from "./MaterialDetailModal";
import SlumpReportModal from "./SlumpReportModal";
import CubeTestReportModal from "./CubeTestReportModal";

interface OverviewViewProps {
  grade: string;
  batchId: string;
  plant: string;
  timestamp: string;
  status: "optimal" | "acceptable" | "review";
  qualitySignals: any[];
  slumpValue: number;
  slumpUnit: string;
  slumpRange: string;
  slumpStatus: "optimal" | "acceptable" | "review";
  slumpImage: string;
  materialLayers: any[];
  cubeTests: any[];
  currentStrength: number;
  requiredStrength: number;
  strengthUnit: string;
  beforeImage: string;
  afterImage: string;
  qualityInsights: any[];
  batchComparison: any;
  onNavigateToAnalytics?: () => void;
}

export default function OverviewView({
  grade,
  batchId,
  plant,
  timestamp,
  status,
  slumpValue,
  slumpUnit,
  slumpRange,
  slumpStatus,
  slumpImage,
  materialLayers,
  cubeTests,
  currentStrength,
  requiredStrength,
  strengthUnit,
  beforeImage,
  afterImage,
  qualityInsights,
  batchComparison,
  onNavigateToAnalytics,
}: OverviewViewProps) {
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "info";
  } | null>(null);
  const [slumpReportModalOpen, setSlumpReportModalOpen] =
    useState(false);
  const [cubeTestReportModalOpen, setCubeTestReportModalOpen] =
    useState(false);
  const [imageGalleryModalOpen, setImageGalleryModalOpen] =
    useState(false);
  const [materialDetailModalOpen, setMaterialDetailModalOpen] =
    useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<
    string | null
  >(null);

  const handleRefresh = () => {
    setToast({
      message: "Refreshing batch data...",
      type: "info",
    });
    setTimeout(() => {
      setToast({
        message: "Data refreshed successfully",
        type: "success",
      });
    }, 1500);
  };

  const handleDownloadReport = () => {
    setSlumpReportModalOpen(true);
  };

  const handleViewImages = () => {
    setImageGalleryModalOpen(true);
  };

  const handleDownloadPDF = () => {
    setCubeTestReportModalOpen(true);
  };

  const handleViewAnalytics = () => {
    if (onNavigateToAnalytics) {
      onNavigateToAnalytics();
    } else {
      setToast({
        message: "Loading full analytics dashboard...",
        type: "info",
      });
    }
  };

  const handleViewInsights = () => {
    setToast({
      message: "Opening quality insights panel...",
      type: "info",
    });
  };

  const handleMaterialClick = (materialName: string) => {
    setSelectedMaterial(materialName);
    setMaterialDetailModalOpen(true);
  };

  const isPassing = currentStrength >= requiredStrength;
  const avg =
    batchComparison.recentBatches.reduce(
      (sum: number, b: any) => sum + b.strength,
      0,
    ) / batchComparison.recentBatches.length;
  const variance =
    ((batchComparison.currentBatch.strength - avg) / avg) * 100;

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-32">
      {/* Navy Blue Header */}
      <div className="bg-gradient-to-br from-[#1B2A5B] to-[#0A0F1C] text-white px-5 pt-6 pb-8">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div
              className="text-white/60 mb-1"
              style={{
                fontSize: "13px",
                letterSpacing: "0.01em",
              }}
            >
              Grade {grade}
            </div>
            <h1
              className="text-white"
              style={{
                fontSize: "32px",
                fontWeight: "700",
                letterSpacing: "-0.025em",
              }}
            >
              {batchId}
            </h1>
          </div>
          {status === "optimal" && (
            <div
              className="px-3 py-1.5 rounded-full backdrop-blur-xl flex items-center gap-1.5"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                border: "1px solid rgba(255, 255, 255, 0.25)",
                boxShadow:
                  "0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
              }}
            >
              <CheckCircle className="w-3.5 h-3.5 text-white" />
              <span
                className="text-white"
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                Approved
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="p-5 space-y-5 -mt-4">
        {/* Batch Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-[#F6F7F9] rounded-lg p-5"
          style={{
            boxShadow:
              "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
          }}
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-[#717182] mt-1" />
              <div className="flex-1">
                <div
                  className="text-[#717182] mb-0.5"
                  style={{
                    fontSize: "10px",
                    fontWeight: "600",
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                  }}
                >
                  Plant Location
                </div>
                <div
                  className="text-[#00315c]"
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                  }}
                >
                  {plant}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="w-4 h-4 text-[#717182] mt-1" />
              <div className="flex-1">
                <div
                  className="text-[#717182] mb-0.5"
                  style={{
                    fontSize: "10px",
                    fontWeight: "600",
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                  }}
                >
                  Cast Time
                </div>
                <div
                  className="text-[#00315c]"
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                  }}
                >
                  {timestamp}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Slump Test */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.4 }}
          className="bg-white rounded-lg overflow-hidden"
          style={{
            boxShadow:
              "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
          }}
        >
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2
                  className="text-[#00315c] mb-1"
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    letterSpacing: "0em",
                  }}
                >
                  Slump Test
                </h2>
                <div
                  className="text-gray-400"
                  style={{ fontSize: "12px" }}
                >
                  Workability Analysis
                </div>
              </div>
              <div
                className="px-3 py-1.5 rounded-full backdrop-blur-xl flex items-center gap-1.5"
                style={{
                  backgroundColor: "rgba(0, 49, 92, 0.08)",
                  border: "1px solid rgba(0, 49, 92, 0.15)",
                  boxShadow:
                    "0 2px 8px rgba(0, 49, 92, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                  color: "#00315c",
                }}
              >
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: "700",
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                  }}
                >
                  Optimal
                </span>
              </div>
            </div>

            <div className="bg-[#F6F7F9] rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <div
                    className="text-gray-400 mb-1"
                    style={{
                      fontSize: "10px",
                      fontWeight: "600",
                      letterSpacing: "0.02em",
                      textTransform: "uppercase",
                    }}
                  >
                    Measured
                  </div>
                  <div
                    className="text-[#00315c]"
                    style={{
                      fontSize: "20px",
                      fontWeight: "700",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {slumpValue} {slumpUnit}
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="text-gray-400 mb-1"
                    style={{
                      fontSize: "10px",
                      fontWeight: "600",
                      letterSpacing: "0.02em",
                      textTransform: "uppercase",
                    }}
                  >
                    Target Range
                  </div>
                  <div
                    className="text-[#00315c]"
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    {slumpRange}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-[#F6F7F9] rounded-lg p-3 text-center">
                <div
                  className="text-gray-400 mb-1"
                  style={{
                    fontSize: "10px",
                    fontWeight: "600",
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                  }}
                >
                  Density
                </div>
                <div
                  className="text-[#00315c]"
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    letterSpacing: "-0.02em",
                  }}
                >
                  2450
                </div>
                <div
                  className="text-gray-400"
                  style={{ fontSize: "11px" }}
                >
                  kg/m³
                </div>
              </div>
              <div className="bg-[#F6F7F9] rounded-lg p-3 text-center">
                <div
                  className="text-gray-400 mb-1"
                  style={{
                    fontSize: "10px",
                    fontWeight: "600",
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                  }}
                >
                  W/C Ratio
                </div>
                <div
                  className="text-[#00315c]"
                  style={{
                    fontSize: "18px",
                    fontWeight: "700",
                    letterSpacing: "-0.02em",
                  }}
                >
                  0.45
                </div>
                <div
                  className="text-gray-400"
                  style={{ fontSize: "11px" }}
                >
                  ratio
                </div>
              </div>
            </div>
          </div>

          <div className="px-5 pb-5">
            <ImageWithFallback
              src={slumpImage}
              alt="Slump test"
              className="w-full h-52 object-cover rounded-lg"
            />
          </div>

          <div className="px-5 pb-5">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-3.5 rounded transition-all flex items-center justify-center gap-2"
              style={{
                backgroundColor: "#00315c",
                color: "white",
                fontSize: "13px",
                fontWeight: "600",
                boxShadow: "0 4px 12px rgba(0, 49, 92, 0.25)",
              }}
              onClick={handleDownloadReport}
            >
              <Download className="w-5 h-5" />
              Download Report
            </motion.button>
          </div>
        </motion.div>

        {/* Compressive Strength */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="bg-white rounded-lg overflow-hidden"
          style={{
            boxShadow:
              "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
          }}
        >
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2
                  className="text-[#00315c] mb-1"
                  style={{
                    fontSize: "15px",
                    fontWeight: "700",
                    letterSpacing: "-0.01em",
                  }}
                >
                  Compressive Strength
                </h2>
                <div
                  className="text-[#00315c]"
                  style={{
                    fontSize: "13px",
                    fontWeight: "600",
                    opacity: 0.7,
                  }}
                >
                  Cube Test Results
                </div>
              </div>
              {isPassing && (
                <div
                  className="px-3 py-1.5 rounded-full backdrop-blur-xl flex items-center gap-1.5"
                  style={{
                    backgroundColor: "rgba(0, 49, 92, 0.08)",
                    border: "1px solid rgba(0, 49, 92, 0.15)",
                    boxShadow:
                      "0 2px 8px rgba(0, 49, 92, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                    color: "#00315c",
                  }}
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span
                    style={{
                      fontSize: "10px",
                      fontWeight: "700",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                    }}
                  >
                    Passed
                  </span>
                </div>
              )}
            </div>

            <div className="bg-[#F6F7F9] rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div
                    className="text-[#717182] mb-1"
                    style={{
                      fontSize: "10px",
                      fontWeight: "600",
                      letterSpacing: "0.02em",
                      textTransform: "uppercase",
                    }}
                  >
                    Current Strength
                  </div>
                  <div
                    className="text-[#00315c]"
                    style={{
                      fontSize: "24px",
                      fontWeight: "700",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {currentStrength}{" "}
                    <span
                      className="text-[#717182]"
                      style={{ fontSize: "16px" }}
                    >
                      {strengthUnit}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="text-[#717182] mb-1"
                    style={{
                      fontSize: "10px",
                      fontWeight: "600",
                      letterSpacing: "0.02em",
                      textTransform: "uppercase",
                    }}
                  >
                    Target
                  </div>
                  <div
                    className="text-[#00315c]"
                    style={{
                      fontSize: "20px",
                      fontWeight: "700",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {requiredStrength}{" "}
                    <span
                      className="text-[#717182]"
                      style={{ fontSize: "14px" }}
                    >
                      {strengthUnit}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mb-3">
                {cubeTests.map((test) => (
                  <div
                    key={test.day}
                    className={`flex-1 h-3 rounded-full ${
                      test.status === "completed"
                        ? "bg-[#00315c]"
                        : "bg-slate-200"
                    }`}
                  />
                ))}
              </div>

              <div className="flex justify-between">
                {cubeTests.map((test) => (
                  <div key={test.day} className="text-center">
                    <div
                      className="text-[#00315c] mb-0.5"
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                      }}
                    >
                      {test.status === "completed"
                        ? test.strength
                        : "—"}
                    </div>
                    <div
                      className="text-[#717182]"
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {test.day}d
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <ImageWithFallback
                src={beforeImage}
                alt="Before test"
                className="w-full h-28 object-cover rounded-lg"
              />
              <ImageWithFallback
                src={afterImage}
                alt="After test"
                className="w-full h-28 object-cover rounded-lg"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="py-3 rounded transition-all flex items-center justify-center gap-2"
                style={{
                  backgroundColor: "#F6F7F9",
                  color: "#00315c",
                  fontSize: "13px",
                  fontWeight: "600",
                }}
                onClick={handleViewImages}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 4C2 2.9 2.9 2 4 2H6C7.1 2 8 2.9 8 4V6C8 7.1 7.1 8 6 8H4C2.9 8 2 7.1 2 6V4ZM10 4C10 2.9 10.9 2 12 2H14C15.1 2 16 2.9 16 4V6C16 7.1 15.1 8 14 8H12C10.9 8 10 7.1 10 6V4ZM2 12C2 10.9 2.9 10 4 10H6C7.1 10 8 10.9 8 12V14C8 15.1 7.1 16 6 16H4C2.9 16 2 15.1 2 14V12Z"
                    fill="currentColor"
                  />
                </svg>
                View Images
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="py-3 rounded transition-all flex items-center justify-center gap-2"
                style={{
                  backgroundColor: "#00315c",
                  color: "white",
                  fontSize: "13px",
                  fontWeight: "600",
                  boxShadow:
                    "0 4px 12px rgba(0, 49, 92, 0.25)",
                }}
                onClick={handleDownloadPDF}
              >
                <Download className="w-4 h-4" />
                Download PDF
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Material Quality */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="bg-white rounded-lg p-5"
          style={{
            boxShadow:
              "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2
                className="text-[#00315c] mb-1"
                style={{
                  fontSize: "15px",
                  fontWeight: "700",
                  letterSpacing: "-0.01em",
                }}
              >
                Material Quality
              </h2>
              <div
                className="text-[#717182]"
                style={{ fontSize: "13px" }}
              >
                {materialLayers.length} Components Verified
              </div>
            </div>
            <div
              className="px-3 py-1.5 rounded-full backdrop-blur-xl flex items-center gap-1.5"
              style={{
                backgroundColor: "rgba(0, 49, 92, 0.08)",
                border: "1px solid rgba(0, 49, 92, 0.15)",
                boxShadow:
                  "0 2px 8px rgba(0, 49, 92, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                color: "#00315c",
              }}
            >
              <CheckCircle className="w-3.5 h-3.5" />
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: "700",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                All Clear
              </span>
            </div>
          </div>

          <div className="space-y-2">
            {materialLayers.map((layer, index) => (
              <motion.button
                key={layer.id}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center justify-between p-3 rounded-lg transition-all"
                style={{
                  backgroundColor: "#F6F7F9",
                  border: "1px solid rgba(0, 49, 92, 0.05)",
                }}
                onClick={() => handleMaterialClick(layer.name)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#00315c]" />
                  <div className="text-left">
                    <div
                      className="text-[#00315c] mb-0.5"
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                      }}
                    >
                      {layer.name}
                    </div>
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "11px" }}
                    >
                      {layer.quality}
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-[#717182]" />
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Performance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="bg-white rounded-lg p-5"
          style={{
            boxShadow:
              "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
          }}
        >
          <div className="mb-4">
            <h2
              className="text-[#00315c] mb-1"
              style={{
                fontSize: "15px",
                fontWeight: "700",
                letterSpacing: "-0.01em",
              }}
            >
              Performance Analysis
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-[#F6F7F9] rounded-lg p-3 text-center">
              <div
                className="text-[#717182] mb-1"
                style={{
                  fontSize: "10px",
                  fontWeight: "600",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                Current
              </div>
              <div
                className="text-[#00315c]"
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  letterSpacing: "-0.02em",
                }}
              >
                {batchComparison.currentBatch.strength}
              </div>
              <div
                className="text-[#717182]"
                style={{ fontSize: "11px" }}
              >
                MPa
              </div>
            </div>

            <div className="bg-[#F6F7F9] rounded-lg p-3 text-center">
              <div
                className="text-[#717182] mb-1"
                style={{
                  fontSize: "10px",
                  fontWeight: "600",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                Average
              </div>
              <div
                className="text-[#00315c]"
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  letterSpacing: "-0.02em",
                }}
              >
                {avg.toFixed(1)}
              </div>
              <div
                className="text-[#717182]"
                style={{ fontSize: "11px" }}
              >
                MPa
              </div>
            </div>

            <div
              className="rounded-lg p-3 text-center"
              style={{
                backgroundColor:
                  variance >= 0 ? "rgba(0, 49, 92, 0.08)" : "#991B1B08",
              }}
            >
              <div
                className="mb-1"
                style={{
                  fontSize: "10px",
                  fontWeight: "600",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                  color: variance >= 0 ? "#00315c" : "#991B1B",
                }}
              >
                {variance >= 0 ? "Above Avg" : "Below Avg"}
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  letterSpacing: "-0.02em",
                  color: variance >= 0 ? "#00315c" : "#991B1B",
                }}
              >
                {variance >= 0 ? "+" : ""}
                {variance.toFixed(1)}%
              </div>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full mt-4 py-3.5 rounded transition-all flex items-center justify-center gap-2"
            style={{
              backgroundColor: "#00315c",
              color: "white",
              fontSize: "13px",
              fontWeight: "600",
              boxShadow: "0 4px 12px rgba(0, 49, 92, 0.25)",
            }}
            onClick={handleViewAnalytics}
          >
            <TrendingUp className="w-4 h-4" />
            View Full Analytics
          </motion.button>
        </motion.div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <SlumpReportModal
        isOpen={slumpReportModalOpen}
        onClose={() => setSlumpReportModalOpen(false)}
        data={{
          batchId,
          grade,
          plant,
          timestamp,
          slumpValue,
          slumpUnit,
          slumpRange,
          slumpStatus:
            slumpStatus === "optimal"
              ? "Optimal"
              : slumpStatus === "acceptable"
                ? "Acceptable"
                : "Review Required",
          slumpImage,
          temperature: 28,
          density: 2450,
          wcRatio: 0.45,
        }}
      />

      <CubeTestReportModal
        isOpen={cubeTestReportModalOpen}
        onClose={() => setCubeTestReportModalOpen(false)}
        data={{
          batchId,
          grade,
          plant,
          timestamp,
          cubeTests,
          currentStrength,
          requiredStrength,
          strengthUnit,
          beforeImage,
          afterImage,
        }}
      />

      <ImageGalleryModal
        isOpen={imageGalleryModalOpen}
        onClose={() => setImageGalleryModalOpen(false)}
        images={[
          {
            url: beforeImage,
            label: "Cube Sample - Before Testing",
            timestamp: timestamp,
          },
          {
            url: afterImage,
            label: "Cube Sample - After Compression",
            timestamp: timestamp,
          },
        ]}
        title="Cube Test Images"
      />

      <MaterialDetailModal
        isOpen={materialDetailModalOpen}
        onClose={() => setMaterialDetailModalOpen(false)}
        material={
          selectedMaterial
            ? {
                id:
                  materialLayers.find(
                    (m) => m.name === selectedMaterial,
                  )?.id || "",
                name: selectedMaterial,
                quality:
                  materialLayers.find(
                    (m) => m.name === selectedMaterial,
                  )?.quality || "",
                compliance: "compliant",
                imageUrl:
                  materialLayers.find(
                    (m) => m.name === selectedMaterial,
                  )?.imageUrl || "",
                specifications: {
                  grade:
                    selectedMaterial === "Cement"
                      ? "OPC 53 Grade"
                      : undefined,
                  supplier:
                    selectedMaterial === "Cement"
                      ? "UltraTech Cement Ltd."
                      : "Local Supplier",
                  batchNumber: `MAT-${Math.floor(Math.random() * 10000)}`,
                  quantity:
                    selectedMaterial === "Cement"
                      ? "380 kg/m³"
                      : selectedMaterial === "Coarse Aggregate"
                        ? "1165 kg/m³"
                        : "680 kg/m³",
                  testDate: timestamp,
                  source:
                    selectedMaterial === "Cement"
                      ? "Certified Testing Lab"
                      : "Quality Control Lab",
                },
              }
            : null
        }
      />
    </div>
  );
}